import {CommentsCategory} from '@domain/comments'
import {useNotification} from '@hconnect/uikit/src/common'
import {MutateOptions, useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {mutations} from '../../../api/mutations'
import {notifyIfErrorMessage} from '../../../common'
import {PlannerQueryClient} from '../../../components/providers/QueryProvider'
import {ErrorResponse} from '../../../interfaces/api'
import {useUrlParam} from '../../useUrlParam'

export const useEditMaterialOrderComment = () => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {notify} = useNotification()
  const raiseError = useErrorHandler()
  const {mutate, isPending: isLoading} = useMutation<
    void,
    AxiosError<ErrorResponse>,
    Parameters<typeof mutations.editComment>[0]
  >({
    mutationFn: mutations.editComment,
    mutationKey: ['editMaterialOrderComment'],
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.message, raiseError, notify)
    },
    onSuccess: () => {
      notify('success', t('success.editMaterialOrderComment'))
    },
    onSettled: () => {
      void PlannerQueryClient.invalidateQueries({
        queryKey: ['comments', {commentsCategory: CommentsCategory.MaterialOrders}]
      })
    }
  })

  const memoizedEditMaterialOrderComment = useCallback(
    (
      params: {comment: string; orderId: number; commentId: string},
      options?: MutateOptions<
        void,
        AxiosError<ErrorResponse>,
        Parameters<typeof mutations.addComment>[0]
      >
    ) =>
      mutate(
        {
          commentId: params.commentId,
          commentsCategoryInstanceId: String(params.orderId),
          commentDto: {value: params.comment},
          commentsCategory: CommentsCategory.MaterialOrders,
          plantCode
        },
        options
      ),
    [mutate, plantCode]
  )

  return {
    editMaterialOrderComment: memoizedEditMaterialOrderComment,
    isEditCommentLoading: isLoading
  }
}
